// Single source of truth for the legal-document HTML sanitizer contract
// (TCX-823). Consumed by BOTH sides of the pipeline:
//   - BE: packages/nestJs/src/modules/legal-documents/legal-documents.sanitize.ts
//     (sanitize-html, runs at publish time before the HTML is persisted)
//   - FE: packages/website/src/helpers/sanitizeHtml.ts
//     (DOMPurify, defense-in-depth at render time on the public Terms/Privacy
//     pages and admin preview)
// Both sanitizers derive their configs from these exports, so the two
// allowlists cannot drift apart.
//
// This module MUST stay dependency-free (zero imports, no DOM/Node types):
// the website bundles it directly via the `@legal-html-contract` alias and it
// is type-checked by both the NestJS tsconfig and the website's strict React
// tsconfig.

// Allowlist matches exactly what the admin Tiptap editor can produce
// (H1/H2/H3, paragraphs, bold/italic/underline/strike, links,
// ordered/unordered lists, blockquotes, tables). Anything outside this set is
// stripped, so a compromised or buggy client can never store
// script/style/event-handler payloads that later render in a re-acceptance
// modal or public page.
export const LEGAL_ALLOWED_TAGS = [
  'h1',
  'h2',
  'h3',
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'ul',
  'ol',
  'li',
  'a',
  'blockquote',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
] as const;

// Per-tag attribute allowlist (sanitize-html shape). Anchors may carry
// href/target/rel (target/rel are then forced to the safe values below);
// table cells may carry colspan/rowspan. Nothing else, on any tag.
export const LEGAL_ALLOWED_ATTRIBUTES = {
  a: ['href', 'target', 'rel'],
  th: ['colspan', 'rowspan'],
  td: ['colspan', 'rowspan'],
} as const;

// URL schemes an href may use. Nothing else (no javascript:, tel:, data:, …).
export const LEGAL_ALLOWED_SCHEMES = ['http', 'https', 'mailto'] as const;

// Every anchor is unconditionally rewritten to these values by both
// sanitizers (new-tab + reverse-tabnabbing protection).
export const LEGAL_ANCHOR_TARGET = '_blank';
export const LEGAL_ANCHOR_REL = 'noopener noreferrer';

// Flat, de-duplicated union of every allowed attribute across all tags —
// derived from LEGAL_ALLOWED_ATTRIBUTES above, never hand-written. DOMPurify's
// ALLOWED_ATTR is a flat global list, so the FE consumes this shape.
export const LEGAL_ALLOWED_ATTRIBUTE_UNION: readonly string[] = Array.from(
  new Set(Object.values(LEGAL_ALLOWED_ATTRIBUTES).flat() as readonly string[]),
);
